import React, { useEffect, useCallback } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Users } from 'lucide-react';
import { useDashboard } from '../../../contexts/DashboardContext';
import { ChartCard, ChartGrid, CustomTooltip, CHART_COLORS, formatCurrency } from '../ChartComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerInsightsTab = () => {
  const { dataCache, setTabData, loading, setTabLoading, errors, setTabError, filters, isTabCached } = useDashboard();
  
  const data = dataCache.customers;

  const fetchData = useCallback(async () => {
    if (isTabCached('customers')) return;
    
    setTabLoading('customers', true);
    setTabError('customers', null);
    
    try {
      const params = new URLSearchParams();
      if (filters.asOnDate) params.append('as_on_date', filters.asOnDate);
      if (filters.branch) params.append('branch', filters.branch);
      
      const response = await fetch(`${API_URL}/api/dashboard/customer-insights?${params}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setTabData('customers', result);
    } catch (err) {
      setTabError('customers', err.message);
    } finally {
      setTabLoading('customers', false);
    }
  }, [filters, isTabCached, setTabData, setTabLoading, setTabError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading.customers) {
    return (
      <ChartGrid>
        {[1, 2, 3, 4].map(i => <ChartCard key={i} title="" loading={true} />)}
      </ChartGrid>
    );
  }

  if (errors.customers) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Error loading data: {errors.customers}</p>
        <button onClick={fetchData} className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { charts, summary } = data;

  return (
    <ChartGrid>
      {/* Chart 1: Top 10 Borrowers (Horizontal Bar) */}
      <ChartCard title="Top 10 Borrowers" subtitle="Highest loan exposure">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.top_borrowers} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} stroke="#94a3b8" width={90} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.danger} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 2: Top 10 Depositors (Horizontal Bar) */}
      <ChartCard title="Top 10 Depositors" subtitle="Highest deposit balances">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.top_depositors} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(1)}Cr`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} stroke="#94a3b8" width={90} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.secondary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 3: Customers with Both Products (KPI Card) */}
      <ChartCard title="Cross-Sell Metrics" subtitle="Customers with both loans and deposits">
        <div className="h-64 flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center mb-4">
            <Users className="w-10 h-10 text-white" />
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-slate-900">{summary.customers_with_both?.toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-1">Customers with Both Products</p>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-6 text-center">
            <div>
              <p className="text-xl font-semibold text-slate-800">{summary.unique_loan_customers?.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Loan Customers</p>
            </div>
            <div>
              <p className="text-xl font-semibold text-slate-800">{summary.unique_deposit_customers?.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Deposit Customers</p>
            </div>
          </div>
        </div>
      </ChartCard>

      {/* Chart 4: Customer Net Position Distribution (Bar) */}
      <ChartCard title="Customer Net Position" subtitle="Net borrowers vs net depositors">
        <div className="h-64">
          {charts.net_position && charts.net_position.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.net_position}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {charts.net_position.map((entry, index) => (
                    <Bar key={index} fill={entry.name.includes('Borrower') ? CHART_COLORS.danger : CHART_COLORS.primary} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <p className="text-sm">Top 10 Concentration Ratio</p>
              <p className="text-3xl font-bold text-slate-700 mt-2">{summary.top_10_concentration}%</p>
              <p className="text-xs mt-1">of total portfolio in top 10 borrowers</p>
            </div>
          )}
        </div>
      </ChartCard>
    </ChartGrid>
  );
};

export default CustomerInsightsTab;
